import { Spin } from 'antd';
import { LOGIN } from 'configs/routeNames';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebaseConfig';
import { onGetToken } from 'utils/axios';
import { useAutoLogout } from './hooks/useAutoLogout';
import { useContractModal } from './hooks/useContractModal';
import { ContractProvider } from './contexts/ContractContext';

const AuthComponent: React.FC<PropsWithChildren> = (props: PropsWithChildren) => {
  const { children } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useAutoLogout();

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        const user = auth.currentUser;
        const authorizationToken = await onGetToken();

        if (mounted) {
          if (!user || !authorizationToken) {
            // Only redirect if we're not already on the login page
            if (!location.pathname.includes(LOGIN)) {
              navigate(`/${LOGIN}`, {
                state: {
                  from: {
                    pathname: location.pathname,
                    search: location.search,
                  },
                },
                replace: true,
              });
            }
          }
          setLoading(false);
        }
      } catch (error) {
        if (mounted) {
          setLoading(false);
          // Handle error appropriately
        }
      }
    };

    const unsubscribe = onAuthStateChanged(auth, () => {
      checkAuth();
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [location.pathname, location.search]);

  // Use a more efficient loading state
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-75">
        <Spin size="large" data-testid="loading-spinner" />
      </div>
    );
  }

  return (
    <ContractProvider>
      {children}
      <ContractModalWrapper />
    </ContractProvider>
  );
};

// Create a separate component for the modal
const ContractModalWrapper = () => {
  const ContractModal = useContractModal();
  return ContractModal;
};

export default AuthComponent;
